/**
 * Boundary mappers for console data. uc-core returns Postgres numerics as decimal STRINGS
 * ("46.100" kg, "2790.00" ₹) — sometimes as numbers — and mixes snake_case (raw SQL rows) with
 * camelCase (serialised entities). Everything the console keeps is integer grams and integer
 * paise; these helpers convert once, exactly (string arithmetic, no floats), at the edge.
 */
import type { Grams } from '@/lib/weight'
import type { Paise } from '@/lib/money'

type Raw = string | number | bigint | null | undefined

/**
 * A decimal ("-12.3456", 46.1, "1e3" refused) → integer minor units at `scale` decimals, rounded
 * half away from zero when the input carries more decimals than `scale`. null for empty/invalid.
 */
export function decimalToMinor(value: Raw, scale: number): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'bigint') return Number(value * 10n ** BigInt(scale))
  let text = typeof value === 'number' ? String(value) : value.trim()
  if (typeof value === 'number' && !Number.isFinite(value)) return null
  if (/e/i.test(text)) {
    // Number like 1e-7 — only reachable from a float; format it plainly first.
    if (typeof value !== 'number') return null
    text = value.toFixed(scale + 1)
  }
  const m = /^([+-])?(\d*)(?:\.(\d*))?$/.exec(text.replace(/,/g, ''))
  if (!m) return null
  const [, sign, whole = '', frac = ''] = m
  if (whole === '' && frac === '') return null
  const kept = frac.slice(0, scale).padEnd(scale, '0')
  const rest = frac.slice(scale)
  let minor = BigInt(whole || '0') * 10n ** BigInt(scale) + BigInt(kept || '0')
  if (rest.length > 0 && Number(rest[0]) >= 5) minor += 1n
  if (sign === '-') minor = -minor
  const n = Number(minor)
  return Number.isSafeInteger(n) ? n : null
}

/** kg decimal → integer grams (null when missing). */
export function kgToGrams(value: Raw): Grams | null {
  return decimalToMinor(value, 3)
}
/** kg decimal → integer grams, 0 when missing. */
export function kgToGrams0(value: Raw): Grams {
  return kgToGrams(value) ?? 0
}
/** ₹ decimal → integer paise (null when missing). */
export function inrToPaise(value: Raw): Paise | null {
  return decimalToMinor(value, 2)
}
/** ₹ decimal → integer paise, 0 when missing. */
export function inrToPaise0(value: Raw): Paise {
  return inrToPaise(value) ?? 0
}

/** Integer minor units → decimal string with `scale` decimals (grams → "46.100"). */
export function minorToDecimal(minor: number | bigint, scale: number): string {
  const b = typeof minor === 'bigint' ? minor : BigInt(minor)
  const neg = b < 0n
  const abs = neg ? -b : b
  const unit = 10n ** BigInt(scale)
  const whole = abs / unit
  const frac = (abs % unit).toString().padStart(scale, '0')
  return `${neg ? '-' : ''}${whole}${scale > 0 ? `.${frac}` : ''}`
}
/** grams → kg string for request bodies ("46.100"). */
export const gramsToKgString = (g: Grams) => minorToDecimal(g, 3)
/** paise → ₹ string for request bodies ("2790.00"). */
export const paiseToInrString = (p: Paise) => minorToDecimal(p, 2)

export function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}
export function numOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = num(value, Number.NaN)
  return Number.isNaN(n) ? null : n
}
export function str(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback
  return String(value)
}
export function strOrNull(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  return String(value)
}
export function bool(value: unknown): boolean {
  return value === true || value === 'true' || value === 't' || value === 1
}
export function boolOrNull(value: unknown): boolean | null {
  if (value === null || value === undefined) return null
  return bool(value)
}
/** ISO string for a Date / string timestamp; null when missing. */
export function iso(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return value.toISOString()
  return String(value)
}
export function arr<T = Record<string, unknown>>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}
export function obj(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}
/** Comma-separated hold reasons → list. */
export function reasons(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value !== 'string' || !value) return []
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}
/** Pick the first present key (snake_case or camelCase variant). */
export function pick(row: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) if (row[k] !== undefined) return row[k]
  return undefined
}
/** Name of a populated relation ({ id, name }) or its id string. */
export function relId(value: unknown): string | null {
  if (!value) return null
  if (typeof value === 'string') return value
  return strOrNull(obj(value).id)
}
export function personName(value: unknown): string | null {
  const o = obj(value)
  const n = [o.firstName ?? o.first_name, o.lastName ?? o.last_name].filter(Boolean).join(' ')
  return n || strOrNull(o.fullName ?? o.name ?? o.username)
}
